import { request, ConsoleLogger } from '@nextgen-web-framework/core';
import type { HttpAugments, RequestOptions } from '@nextgen-web-framework/core';
import { v5 as uuidv5 } from 'uuid';
import type { CreatePaymentRequest, PaymentDetails } from '@commerce-payment-webcomponents/sdk'; // TODO: This is not a profile API endpoint, remove later
// import StorageService from '@nextgen-web-framework/core/storage';
import {
  postAddressReqObj,
  getAddressListReqObj,
  putAddressListReqObj,
  getProfilesReqObj,
  getSearchSponsorReqObj,
  postRegistrationObj,
  getRegistrationObj,
  patchRegistrationObj,
  postSubmitRegistrationObj,
  getTaskListReqObj,
  getContactSearchObj,
  postProfileChallengesObj,
  postAccountChallengesObj,
  patchProfilesReqObj,
  postRegistrationChallengesObj,
  deleteAddressListReqObj,
  getRegionAddressSearchObj,
  getContractListReqObj,
  getAccountReqObj,
  getPublicationReqObj,
  patchPublicationReqObj,
  getAccountPhonesReqObj,
  getAccountEmailsReqObj,
  postAccountPhoneReqObj,
  postAccountEmailReqObj,
  patchAccountPhoneReqObj,
  patchAccountEmailReqObj,
  deleteAccountPhoneReqObj,
  deleteAccountEmailReqObj,
  getAddressByContactIdReqObj,
  postPaymentReqObj,
  getAccountDetailsReqObj, // TODO: This is not a profile API endpoint, remove later
  getAccountAutoRenewalReqObj,
} from '../config';
import {
  Contact,
  CheckUsernameResponse,
  Profile,
  SponsorSearchResponseInner,
  RegistrationCreateRequest,
  RegistrationPatchRequest,
  RegistrationResponse,
  RegistrationSubmitResponse,
  ContactRetrieveResponse,
  CreateChallengeResponse,
  TaskStatus,
  TaskType,
  ContactResponse,
  RegistrationSubmitRequest,
  ProfileList,
  AccountDetail,
  PublicationList,
  PublicationPatchRequest,
  PublicationPatchResponse,
  RegistrationsResponse,
  BaseRegistration,
  PhoneList,
  EmailList,
  PhoneDetail,
  EmailDetail,
  PhoneCreate,
  EmailCreate,
  PhonePatch,
  EmailPatch,
  Search,
  AccountAutoRenew,
} from '../../services/models';
import type {
  CreateShippingAddressProps,
  ShippingAddressListAPI,
  PutShippingAddressListAPI,
  checkUserAPI,
  downlineAddressListAPI,
  SponsorSearchProps,
  RegistrationNextHandlerPatchPayload,
  profileAddressListAPI,
  GetTaskResType,
  CreateProfileChallengeAPIRequest,
  CreateAccountChallengeAPIRequest,
  ProfileUpdateAPI,
  CreateRegistrationChallengeAPIRequest,
  ContactSearchItems,
  ContractDetailListReponse,
  ContractListResponse,
  ContractDetailRequest,
  CustomRequestOptions,
  AddressSearchRequestType,
  AddressSearchResponseType,
  StateData,
  GetAccountDetailsParams,
} from '../../types';
import { getCheckUserExistsReqObj } from '../config/profileApiEndPoints';
import { WithoutPostalCodeLeadsSearchProps } from '../../types/profileTypes';

const API_VERSION_V1 = 'v1';
//will be removed once actual API is implemented
export const profileApi = {
  createShippingDetails({ payload }: CreateShippingAddressProps, options?: CustomRequestOptions): Promise<Contact> {
    const req = postAddressReqObj(API_VERSION_V1);
    if (!payload) {
      return Promise.reject('Please check arguments');
    }
    req.body = payload as unknown as BodyInit;
    return handleRequest(req, options, true);
  },

  getShippingAddressList(
    { contactType, ownerId, accountNumber }: ShippingAddressListAPI,
    options?: CustomRequestOptions,
  ): Promise<ContactResponse> {
    const req = getAddressListReqObj(API_VERSION_V1);
    req.params = {
      contactType,
      ...(ownerId && { ownerId }),
      ...(accountNumber && { accountNumber }),
    };

    return handleRequest(req, options);
  },
  putShippingAddressList(
    { payload, contactId }: PutShippingAddressListAPI,
    options?: CustomRequestOptions,
  ): Promise<Contact> {
    const req = putAddressListReqObj(API_VERSION_V1, contactId);
    if (!contactId || !payload) {
      return Promise.reject('Please check arguments');
    }
    req.body = payload as unknown as BodyInit;

    return handleRequest(req, options, true);
  },

  checkUserExists({ username }: checkUserAPI): Promise<CheckUsernameResponse> {
    const req = getCheckUserExistsReqObj(API_VERSION_V1);
    if (!username) {
      return Promise.reject('Please check arguments');
    }
    req.params = {
      username: username,
    };
    return handleRequest(req);
  },

  getProfiles(options?: CustomRequestOptions): Promise<Profile> {
    const req = getProfilesReqObj(API_VERSION_V1);
    return handleRequest(req, options);
  },
  getDownlineAddressList(
    { contactType, ownerId, accountNumber }: downlineAddressListAPI,
    options?: CustomRequestOptions,
  ): Promise<ContactResponse> {
    const req = getAddressListReqObj(API_VERSION_V1);
    req.params = {
      contactType,
      ownerId,
      accountNumber,
    };
    return handleRequest(req, options);
  },
  getSponsorByAccountNumber(
    { sponsorInputValue, accountType }: SponsorSearchProps,
    options?: CustomRequestOptions,
  ): Promise<SponsorSearchResponseInner[]> {
    const req = getSearchSponsorReqObj(API_VERSION_V1);
    if (!sponsorInputValue || !accountType) {
      return Promise.reject('Please check arguments');
    }
    req.params = {
      regAccountType: accountType,
      accountNumber: sponsorInputValue,
    };
    return handleRequest(req, options);
  },
  getLeadsListByPostalCode(
    { sponsorInputValue, accountType }: SponsorSearchProps,
    options?: CustomRequestOptions,
  ): Promise<SponsorSearchResponseInner[]> {
    const req = getSearchSponsorReqObj(API_VERSION_V1);
    if (!sponsorInputValue || !accountType) {
      return Promise.reject('Please check arguments');
    }
    req.params = {
      regAccountType: accountType,
      postalCd: sponsorInputValue,
    };

    return handleRequest(req, options);
  },

  getLeadsListWithoutPostalCode(
    { accountType, state, city }: WithoutPostalCodeLeadsSearchProps,
    options?: CustomRequestOptions,
  ): Promise<SponsorSearchResponseInner[]> {
    const req = getSearchSponsorReqObj(API_VERSION_V1);
    if (!accountType) {
      return Promise.reject('Please check arguments');
    }
    const params = new URLSearchParams();
    params.append('regAccountType', accountType);
    if (state) {
      params.append('state', state);
    }
    if (city) {
      params.append('city', city);
    }
    req.params = params;
    return handleRequest(req, options);
  },

  getInternationalSponsor(
    {
      sponsorInputValue,
      internationalSponsorAccountNumber,
      internationalSponsorCountryCode,
      accountType,
    }: SponsorSearchProps,
    options?: CustomRequestOptions,
  ): Promise<SponsorSearchResponseInner[]> {
    const req = getSearchSponsorReqObj(API_VERSION_V1);
    if (!sponsorInputValue || !accountType || !internationalSponsorCountryCode || !internationalSponsorAccountNumber) {
      return Promise.reject('Please check arguments');
    }
    req.params = {
      regAccountType: accountType,
      accountNumber: sponsorInputValue,
    };

    if (internationalSponsorAccountNumber && internationalSponsorCountryCode) {
      req.params.internationalSponsorAccountNumber = internationalSponsorAccountNumber;
      req.params.internationalSponsorCountryCode = internationalSponsorCountryCode;
    }
    return handleRequest(req, options);
  },
  createRegistrationId(
    payload: RegistrationCreateRequest,
    options?: CustomRequestOptions,
    useCustomKey?: boolean,
  ): Promise<RegistrationResponse> {
    const req = postRegistrationObj(API_VERSION_V1);
    req.body = payload as unknown as BodyInit;
    return handleRequest(req, options, useCustomKey);
  },
  getRegistration(
    { accountNumber, status, registrationFlow }: BaseRegistration,
    options?: CustomRequestOptions,
  ): Promise<RegistrationsResponse> {
    const req = getRegistrationObj(API_VERSION_V1);
    const params = new URLSearchParams();
    if (accountNumber) {
      params.append('accountNumber', accountNumber.toString());
    }
    if (status) {
      params.append('status', status);
    }
    if (registrationFlow) {
      params.append('registrationFlow', registrationFlow);
    }
    req.params = params;
    return handleRequest(req, options);
  },
  getRegistrationDetailsById(
    registrationId: string,
    options?: CustomRequestOptions,
    projection?: string,
  ): Promise<RegistrationResponse> {
    const req = getRegistrationObj(API_VERSION_V1);
    req.url += `/${registrationId}`;
    if (projection) {
      req.params = {
        projection: projection,
      };
    }
    return handleRequest(req, options);
  },
  async patchRegistration(
    payload: RegistrationPatchRequest | RegistrationNextHandlerPatchPayload,
    registrationId: string,
    stepName: string,
    options?: CustomRequestOptions,
  ): Promise<RegistrationResponse> {
    const req = patchRegistrationObj(API_VERSION_V1);
    req.url += `/${registrationId}`;
    if (stepName) {
      req.params = {
        stepName: stepName,
      };
    }

    req.body = payload as unknown as BodyInit;

    const response: RegistrationResponse = await handleRequest(req, options);
    return response;
  },

  postSubmitRegistration(
    payload: RegistrationSubmitRequest,
    registrationId: string,
    options?: CustomRequestOptions,
  ): Promise<RegistrationSubmitResponse> {
    const req = postSubmitRegistrationObj(API_VERSION_V1, registrationId);
    if (!registrationId) {
      return Promise.reject('Please check arguments');
    }
    req.body = payload as unknown as BodyInit;
    return handleRequest(req, options);
  },

  contactSearch(keyword: string, options?: CustomRequestOptions): Promise<ContactSearchItems> {
    const req = getContactSearchObj(API_VERSION_V1);
    req.params = {
      query: keyword,
    };
    return handleRequest(req, options);
  },
  contactSearchContainer(containerId: string, options?: CustomRequestOptions): Promise<ContactSearchItems> {
    const req = getContactSearchObj(API_VERSION_V1);
    req.params = {
      containerId: containerId,
    };
    return handleRequest(req, options);
  },
  contactSearchMatchId(matchId: string, options?: CustomRequestOptions): Promise<ContactRetrieveResponse> {
    const req = getContactSearchObj(API_VERSION_V1);
    req.url += `/${matchId}`;
    return handleRequest(req, options);
  },
  getProfilesById(profileId: string, options?: CustomRequestOptions): Promise<Profile> {
    const req = getProfilesReqObj(API_VERSION_V1);
    req.url += `/${profileId}`;
    return handleRequest(req, options);
  },
  patchProfilesById({ payload, profileId }: ProfileUpdateAPI, options?: CustomRequestOptions): Promise<Profile> {
    const req = patchProfilesReqObj(API_VERSION_V1);
    req.url += `/${profileId}`;
    req.body = payload as unknown as BodyInit;
    return handleRequest(req, options);
  },
  getProfileAddress(
    { ownerId, isDefault }: profileAddressListAPI,
    options?: CustomRequestOptions,
  ): Promise<ContactResponse> {
    const req = getAddressListReqObj(API_VERSION_V1);
    req.params = {
      ownerId: ownerId,
      isDefault: isDefault ? 'true' : 'false',
    };
    return handleRequest(req, options);
  },
  postProfileAddress({ payload }: CreateShippingAddressProps, options?: CustomRequestOptions): Promise<Contact> {
    const req = postAddressReqObj(API_VERSION_V1);
    req.body = payload as unknown as BodyInit;
    return handleRequest(req, options, true);
  },

  putProfileAddress(
    { payload, contactId }: PutShippingAddressListAPI,
    options?: CustomRequestOptions,
  ): Promise<Contact> {
    const req = putAddressListReqObj(API_VERSION_V1, contactId);
    if (!contactId || !payload) {
      return Promise.reject('Please check arguments');
    }
    req.body = payload as unknown as BodyInit;

    return handleRequest(req, options, true);
  },
  getAddressByContactId(contactId: string, options?: CustomRequestOptions): Promise<Contact> {
    const req = getAddressByContactIdReqObj(API_VERSION_V1, contactId);
    if (!contactId) {
      return Promise.reject('Please check arguments');
    }
    return handleRequest(req, options, true);
  },
  postProfileChallange(
    { payload, profileId }: CreateProfileChallengeAPIRequest,
    options?: CustomRequestOptions,
  ): Promise<CreateChallengeResponse> {
    const req = postProfileChallengesObj(API_VERSION_V1, profileId);
    if (!profileId || !payload) {
      return Promise.reject('Please check arguments');
    }
    req.body = payload as unknown as BodyInit;

    return handleRequest(req, options, true);
  },
  postAccountChallenge(
    { payload, accountNum }: CreateAccountChallengeAPIRequest,
    options?: CustomRequestOptions,
  ): Promise<CreateChallengeResponse> {
    const req = postAccountChallengesObj(API_VERSION_V1, accountNum);
    if (!accountNum || !payload) {
      return Promise.reject('Please check arguments');
    }
    req.body = payload as unknown as BodyInit;

    return handleRequest(req, options, true);
  },

  getTaskList(
    accountNumber: string,
    type?: TaskType,
    status?: TaskStatus,
    options?: CustomRequestOptions,
  ): Promise<GetTaskResType> {
    const req = getTaskListReqObj(API_VERSION_V1);
    req.params = {
      accountNumber: accountNumber,
    };
    if (type) {
      req.params.type = type;
    }
    if (status) {
      req.params.status = status;
    }

    return handleRequest(req, options);
  },
  postRegistrationChallenge(
    { payload, registrationId }: CreateRegistrationChallengeAPIRequest,
    options?: CustomRequestOptions,
  ): Promise<CreateChallengeResponse> {
    const req = postRegistrationChallengesObj(API_VERSION_V1, registrationId);
    if (!registrationId || !payload) {
      return Promise.reject('Please check arguments');
    }
    req.body = payload as unknown as BodyInit;
    return handleRequest(req, options, true);
  },
  deleteShippingAddressList(contactId: string, options?: CustomRequestOptions): Promise<Contact> {
    const req = deleteAddressListReqObj(API_VERSION_V1, contactId);
    if (!contactId) {
      return Promise.reject('Please check arguments');
    }
    return handleRequest(req, options);
  },
  getContractList(accountNumber: string, options?: CustomRequestOptions): Promise<ContractListResponse> {
    const req = getContractListReqObj(API_VERSION_V1);
    req.params = {
      accountNumber,
    };
    return handleRequest(req, options);
  },
  getContractDetailList(
    { contractId, contractType, accountSubTypeCd, seqNo, locale }: ContractDetailRequest,
    options?: CustomRequestOptions,
  ): Promise<ContractDetailListReponse> {
    const req = getContractListReqObj(API_VERSION_V1);
    req.url += `/${contractId}`;
    req.params = {
      accountSubTypeCd: accountSubTypeCd,
      contractType: contractType,
      seqNo: String(seqNo),
      ...(locale && { language: locale }),
    };
    return handleRequest(req, options);
  },
  getAddressRegionData(type: string, language: string, options?: CustomRequestOptions): Promise<StateData[]> {
    // Get all province data
    const req = getRegionAddressSearchObj(API_VERSION_V1, type);

    req.params = {
      language,
    };

    return handleRequest(req, options);
  },
  getAddressRegionSearch(
    type: string,
    payload: AddressSearchRequestType,
    options?: CustomRequestOptions,
  ): Promise<AddressSearchResponseType> {
    // Get District/Sub/PostCode
    const req = getRegionAddressSearchObj(API_VERSION_V1, type);

    req.params = {
      ...payload,
    };

    return handleRequest(req, options);
  },
  getAccountByNum(accountNumber: string, isPrimary?: boolean, options?: CustomRequestOptions): Promise<ProfileList> {
    const req = getAccountReqObj(API_VERSION_V1);
    req.url += `/${accountNumber}/mappings`;
    if (isPrimary !== undefined) {
      req.params = {
        isPrimary: isPrimary ? 'true' : 'false',
      };
    }
    return handleRequest(req, options);
  },
  getAccountInformation(accountNumber: string, options?: CustomRequestOptions): Promise<AccountDetail> {
    const req = getAccountReqObj(API_VERSION_V1);
    req.url += `/${accountNumber}`;
    return handleRequest(req, options);
  },
  getAccountAutoRenewal(accountNumber: string, options?: CustomRequestOptions): Promise<AccountAutoRenew> {
    const req = getAccountAutoRenewalReqObj(API_VERSION_V1, accountNumber);
    return handleRequest(req, options);
  },
  getPublication(accountNumber: string, profileId: string, options?: CustomRequestOptions): Promise<PublicationList> {
    const req = getPublicationReqObj(API_VERSION_V1);
    req.params = {
      accountNumber,
      profileId,
    };
    return handleRequest(req, options);
  },
  patchPublication(
    accountNumber: string,
    profileId: string,
    payload: PublicationPatchRequest,
    options?: CustomRequestOptions,
  ): Promise<PublicationPatchResponse> {
    const req = patchPublicationReqObj(API_VERSION_V1);
    req.params = {
      accountNumber,
      profileId,
    };
    req.body = payload as unknown as BodyInit;

    return handleRequest(req, options);
  },
  getAccountPhones(
    accountNumber: string,
    profileId: string,
    requiredDefaultFlag = true,
    isDeleted = false,
    options?: CustomRequestOptions,
  ): Promise<PhoneList> {
    try {
      const req = getAccountPhonesReqObj(API_VERSION_V1);
      req.params = {
        accountNumber,
        profileId,
        ...(requiredDefaultFlag && { isDefault: 'true' }), // For alternative phone numbers, isDefault is not required
        isDeleted: String(isDeleted),
      };
      return handleRequest(req, options);
    } catch (error) {
      ConsoleLogger.error(error);
      // No record found, return empty array
      return Promise.resolve({ phoneList: [] });
    }
  },
  getAccountEmails(
    accountNumber: string,
    profileId: string,
    isDefault = true,
    isDeleted = false,
    options?: CustomRequestOptions,
  ): Promise<EmailList> {
    try {
      const req = getAccountEmailsReqObj(API_VERSION_V1);
      req.params = {
        accountNumber,
        profileId,
        isDefault: String(isDefault),
        isDeleted: String(isDeleted),
      };
      return handleRequest(req, options);
    } catch (error) {
      ConsoleLogger.error(error);
      // No record found, return empty array
      return Promise.resolve({ emailList: [] });
    }
  },
  postAccountPhone(payload: PhoneCreate, options?: CustomRequestOptions): Promise<PhoneDetail> {
    const req = postAccountPhoneReqObj(API_VERSION_V1);
    req.body = payload as unknown as BodyInit;
    return handleRequest(req, options);
  },
  postAccountEmail(payload: EmailCreate, options?: CustomRequestOptions): Promise<EmailDetail> {
    const req = postAccountEmailReqObj(API_VERSION_V1);
    req.body = payload as unknown as BodyInit;
    return handleRequest(req, options);
  },
  patchAccountPhone(phoneId: string, payload: PhonePatch, options?: CustomRequestOptions): Promise<PhoneDetail> {
    const req = patchAccountPhoneReqObj(API_VERSION_V1, phoneId);
    req.body = payload as unknown as BodyInit;
    return handleRequest(req, options);
  },
  patchAccountEmail(emailId: string, payload: EmailPatch, options?: CustomRequestOptions): Promise<EmailDetail> {
    const req = patchAccountEmailReqObj(API_VERSION_V1, emailId);
    req.body = payload as unknown as BodyInit;
    return handleRequest(req, options);
  },
  deleteAccountPhone(phoneId: string, options?: CustomRequestOptions): Promise<PhoneDetail> {
    const req = deleteAccountPhoneReqObj(API_VERSION_V1, phoneId);
    return handleRequest(req, options);
  },
  deleteAccountEmail(emailId: string, options?: CustomRequestOptions): Promise<EmailDetail> {
    const req = deleteAccountEmailReqObj(API_VERSION_V1, emailId);
    return handleRequest(req, options);
  },

  /**
   * @deprecated This is not a profile API endpoint and will be removed in future versions once it's added to registration api.
   */
  postPayment(payload: CreatePaymentRequest, options?: CustomRequestOptions): Promise<PaymentDetails> {
    const req = postPaymentReqObj(API_VERSION_V1);
    req.body = payload as unknown as BodyInit;
    return handleRequest(req, options);
  },

  getAccountDetails(payload: GetAccountDetailsParams, options?: CustomRequestOptions): Promise<Search> {
    const req = getAccountDetailsReqObj(API_VERSION_V1);
    req.params = payload.params;
    return handleRequest(req, options);
  },
};

async function handleRequest<T>(
  req: RequestOptions,
  options?: CustomRequestOptions,
  useCustomKey?: boolean,
): Promise<T> {
  try {
    if (options?.headers) {
      req.headers = options?.headers;
    }
    if (options?.baseURL) {
      req.url = `${options.baseURL}${req.url}`;
    }

    const reqArgs: HttpAugments = useCustomKey
      ? {
          skipIdempotency: false,
          customCache: true,
          customKeyGenerator: () => uuidv5(`${Date.now()}`, uuidv5.URL),
        }
      : {};

    const response: T = await request(req, reqArgs);
    return Promise.resolve(response);
  } catch (error) {
    ConsoleLogger.error(error);
    throw error;
  }
}

export default profileApi;
